import { useState, useEffect } from 'react'
import { clientSigningApi, signingApi } from '@services/api'
import { useFormStore, useFormActions } from '@hooks/useFormStore'
import { ClientRiepilogo } from './ClientRiepilogo'
import { ClientIdentityUpload } from './ClientIdentityUpload'
import { SigningSection, SigningComplete } from '@components/Signing'
import { LoadingScreen } from '@components/UI/LoadingScreen'
import type { ClientSubmission, ClientSigningResponse, SigningSession } from '@/types'
import styles from './ClientSigning.module.css'

interface ClientSigningWrapperProps {
  token: string
}

type ClientStep = 'loading' | 'error' | 'riepilogo' | 'documento' | 'otp_verify' | 'firma' | 'completato'

export function ClientSigningWrapper({ token }: ClientSigningWrapperProps) {
  const [step, setStep] = useState<ClientStep>('loading')
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<ClientSigningResponse | null>(null)
  const [contactType, setContactType] = useState<'email' | 'phone'>('phone')
  const [isStarting, setIsStarting] = useState(false)
  
  const { setSigningSessionId, setSigningSession, setGeneratedDocuments, setSubmissionId, setData: setFormData } = useFormActions()
  const signingSessionId = useFormStore((state) => state.signingSessionId)
  const formStep = useFormStore((state) => state.step)

  // Sync with Zustand store step for completion transition
  useEffect(() => {
    if (formStep === 'completato') {
      setStep('completato')
    }
  }, [formStep])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [step])

  // Load submission data
  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await clientSigningApi.getSubmission(token)
        setData(result)
        
        // Store submission id, tipo_cliente e prodotti per il rendering
        // della firma cliente (CTE iframe + scelta dei campi firma).
        setSubmissionId(result.submission.id)
        const formPatch: Record<string, unknown> = {}
        if (result.submission.tipo_cliente) {
          formPatch.tipo_cliente = result.submission.tipo_cliente
        }
        if (typeof result.submission.prodotto_luce_id === 'number') {
          formPatch.prodotto_luce_id = result.submission.prodotto_luce_id
        }
        if (typeof result.submission.prodotto_gas_id === 'number') {
          formPatch.prodotto_gas_id = result.submission.prodotto_gas_id
        }
        if (Object.keys(formPatch).length > 0) {
          setFormData(formPatch as Parameters<typeof setFormData>[0])
        }
        
        // Store generated documents
        if (result.documents && result.documents.length > 0) {
          setGeneratedDocuments(result.documents.map(doc => ({
            success: true,
            type: 'cambio_fornitore',
            utility: 'luce',
            filename: doc.filename,
            url: doc.url
          })))
        }
        
        // Check if signing session already exists
        if (result.signing.session_exists && result.signing.session_id) {
          setSigningSessionId(result.signing.session_id)
          
          // Load session details
          const sessionData = await signingApi.getSession(result.signing.session_id)
          // Nascondi il nome del file al cliente: usiamo sempre la stringa
          // generica "Documento Contratto" lato frontend.
          setSigningSession({
            ...sessionData.session,
            document_name: 'Documento Contratto',
          })
          
          // Determine step based on session status
          if (sessionData.session.status === 'completed') {
            setStep('completato')
          } else if (sessionData.session.status === 'verified' || sessionData.session.status === 'signing') {
            setStep('firma')
          } else if (sessionData.session.status === 'otp_sent') {
            setStep('otp_verify')
          } else {
            setStep('riepilogo')
          }
        } else {
          setStep('riepilogo')
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Errore caricamento dati'
        setError(message)
        setStep('error')
      }
    }
    
    loadData()
  }, [token, setSigningSessionId, setSigningSession, setGeneratedDocuments, setSubmissionId])

  const handleProceedFromRiepilogo = async () => {
    if (data?.submission.client_must_upload_identity) {
      try {
        const status = await clientSigningApi.getIdentityStatus(token)
        if (!status.has_required_documents) {
          setStep('documento')
          return
        }
      } catch (err) {
        // Se lo status non è raggiungibile, mostriamo lo step upload per sicurezza.
        setStep('documento')
        const message = err instanceof Error ? err.message : 'Errore caricamento stato documento'
        setError(message)
        return
      }
    }
    handleStartSigning()
  }

  const handleStartSigning = async () => {
    setIsStarting(true)
    setError(null)
    
    try {
      const result = await clientSigningApi.startSigning(token, contactType)
      
      setSigningSessionId(result.session.id)
      setSigningSession({
        id: result.session.id,
        submission_id: result.session.submission_id,
        contact_type: result.session.contact_type,
        contact_value: '',
        client_name: data?.submission.anagrafica.nome || '',
        client_surname: data?.submission.anagrafica.cognome || '',
        document_name: 'Documento Contratto',
        status: result.session.status as SigningSession['status'],
        pdf_url: result.session.pdf_url,
        ha_luce: result.session.ha_luce,
        ha_gas: result.session.ha_gas,
        created_at: result.session.created_at,
        completed_at: null
      })
      
      setStep('otp_verify')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore avvio firma'
      setError(message)
    } finally {
      setIsStarting(false)
    }
  }

  const handleConsentsUpdated = (submission: ClientSubmission) => {
    if (data) {
      setData({
        ...data,
        submission
      })
    }
  }

  if (step === 'loading') {
    return <LoadingScreen />
  }

  if (step === 'error') {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>
            <i className="bi bi-exclamation-triangle" />
          </div>
          <h2>Errore</h2>
          <p>{error || 'Si è verificato un errore'}</p>
          <button 
            type="button" 
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Riprova
          </button>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {/* Header - always visible */}
        <header className={styles.pageHeader}>
          <img src="/assets/logo.webp" alt="Heracomm" className={styles.headerLogo} />
        </header>

        {/* Riepilogo Step */}
        {step === 'riepilogo' && (
          <>
            <ClientRiepilogo 
              submission={data.submission}
              token={token}
              onConsentsUpdated={handleConsentsUpdated}
            />
            
            {/* Contact Selection for OTP */}
            {step === 'riepilogo' && (
              <div className={styles.otpSection}>
                <h3>Come vuoi ricevere il codice di verifica?</h3>
                <div className={styles.contactOptions}>
                  <label className={`${styles.contactOption} ${contactType === 'email' ? styles.contactOptionActive : ''}`}>
                    <input
                      type="radio"
                      name="contact"
                      value="email"
                      checked={contactType === 'email'}
                      onChange={() => setContactType('email')}
                    />
                    <i className="bi bi-envelope" />
                    <div>
                      <span>Email</span>
                      <small>{data.submission.anagrafica.email}</small>
                    </div>
                  </label>
                  <label className={`${styles.contactOption} ${contactType === 'phone' ? styles.contactOptionActive : ''}`}>
                    <input
                      type="radio"
                      name="contact"
                      value="phone"
                      checked={contactType === 'phone'}
                      onChange={() => setContactType('phone')}
                    />
                    <i className="bi bi-phone" />
                    <div>
                      <span>SMS</span>
                      <small>{data.submission.anagrafica.telefono}</small>
                    </div>
                  </label>
                </div>
                
                {error && (
                  <div className={styles.errorBanner}>
                    <i className="bi bi-exclamation-triangle" />
                    <span>{error}</span>
                  </div>
                )}
                
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleProceedFromRiepilogo}
                  disabled={isStarting}
                >
                  {isStarting ? (
                    <>
                      <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send" />
                      Invia Codice e Procedi
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* Documento Identità Step (solo se delegato dall'operatore) */}
        {step === 'documento' && (
          <ClientIdentityUpload
            token={token}
            clientName={data.submission.anagrafica.nome || ''}
            onComplete={handleStartSigning}
          />
        )}

        {/* OTP Verify Step */}
        {step === 'otp_verify' && signingSessionId && (
          <OtpVerifyWrapper 
            sessionId={signingSessionId}
            onVerified={() => setStep('firma')}
            onBack={() => setStep('riepilogo')}
          />
        )}

        {/* Firma Step */}
        {step === 'firma' && (
          <SigningSection />
        )}

        {/* Completato Step */}
        {step === 'completato' && (
          <SigningComplete />
        )}
      </div>
    </div>
  )
}

// OTP Verify wrapper component
interface OtpVerifyWrapperProps {
  sessionId: string
  onVerified: () => void
  onBack: () => void
}

function OtpVerifyWrapper({ sessionId, onVerified, onBack }: OtpVerifyWrapperProps) {
  const [code, setCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const { setSigningSession } = useFormActions()

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Inserisci il codice a 6 cifre')
      return
    }

    setIsVerifying(true)
    setError(null)

    try {
      const result = await signingApi.verifyOtp(sessionId, code)
      if (result.verified) {
        // Update session status
        const { session } = await signingApi.getSession(sessionId)
        setSigningSession(session)
        onVerified()
      } else {
        setError('Codice non valido')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore verifica'
      setError(message)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResend = async () => {
    setIsResending(true)
    setError(null)

    try {
      await signingApi.requestOtp(sessionId)
      setCode('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore reinvio'
      setError(message)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className={styles.otpVerifySection}>
      {/* Progress */}
      <div className={styles.progressSteps}>
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}><i className="bi bi-check" /></div>
          <span>Riepilogo</span>
        </div>
        <div className={styles.progressLine} />
        <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
          <div className={styles.progressNumber}>2</div>
          <span>Verifica</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>3</div>
          <span>Firma</span>
        </div>
      </div>

      <div className={styles.otpCard}>
        <div className={styles.otpIcon}>
          <i className="bi bi-shield-lock" />
        </div>
        <h2>Inserisci il codice</h2>
        <p>Abbiamo inviato un codice di verifica a 6 cifre</p>

        <div className={styles.otpInputWrapper}>
          <input
            type="text"
            className={styles.otpInput}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            autoFocus
          />
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <i className="bi bi-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleVerify}
          disabled={isVerifying || code.length !== 6}
        >
          {isVerifying ? 'Verifica in corso...' : 'Verifica Codice'}
        </button>

        <div className={styles.otpActions}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? 'Invio in corso...' : 'Invia nuovo codice'}
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={onBack}
          >
            Torna indietro
          </button>
        </div>
      </div>
    </div>
  )
}
